import { Injectable, effect, signal, WritableSignal, computed, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { StorageService } from "@app-common/services/storage/storage.service";
import { ShortcutManagerService } from "@app-common/services/shortcut-manager.service";
import { updatePreset, usePreset } from "@primeng/themes";
import { AppTheme } from "src/themes/AppTheme";
import { APP_THEMES, DEFAULT_THEME_ID, ThemeDefinition, ThemeId, SidebarVars, StorefrontVars, getStorefrontVars } from "src/themes/themes";

export type MenuMode =
    | 'static'
    | 'overlay'
    | 'horizontal'
    | 'slim'
    | 'compact'
    | 'reveal'
    | 'drawer';

export type ColorScheme = 'light' | 'dark';

export type CardRadius = 'square' | 'small' | 'medium' | 'large';

export interface AppConfig {
    inputStyle: string;
    theme: ThemeId;
    ripple: boolean;
    menuMode: MenuMode;
    scale: number;
    cardRadius: CardRadius;
}

interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    rightMenuVisible: boolean;
    configSidebarVisible: boolean;
    staticMenuMobileActive: boolean;
    menuHoverActive: boolean;
    searchBarActive: boolean;
    sidebarActive: boolean;
    anchored: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    _config: AppConfig = {
        ripple: true,
        inputStyle: 'outlined',
        menuMode: 'static',
        theme: DEFAULT_THEME_ID,
        scale: 14,
        cardRadius: 'small',
    };


    private defaultTheme: AppConfig = {
        ripple: true,
        inputStyle: 'outlined',
        menuMode: 'static',
        theme: DEFAULT_THEME_ID,
        scale: 14,
        cardRadius: 'medium',
    };


    state = signal<LayoutState>({
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        rightMenuVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        searchBarActive: false,
        sidebarActive: false,
        anchored: false,
    });

    config: WritableSignal<AppConfig>;

    private _initialLoad = true;

    private configUpdate = new Subject<AppConfig>();

    private overlayOpen = new Subject<any>();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    colorScheme = computed(() => this.getThemeById(this.config().theme).colorScheme);

    constructor(private storageService: StorageService,
                private hotkeyManager: ShortcutManagerService,
                private zone: NgZone,
                ) {

        let themePrefs = this.storageService.getItem('themePrefs');
        let config = { ...this.defaultTheme };
        if (themePrefs) {
            try {
                let themeConfig = JSON.parse(themePrefs);

                // Migrate old colorScheme+menuTheme format to unified theme
                if (themeConfig.colorScheme && !themeConfig.theme) {
                    themeConfig.theme = themeConfig.colorScheme === 'dark' ? 'midnight' : 'light';
                    delete themeConfig.colorScheme;
                    delete themeConfig.menuTheme;
                }

                config = { ...config, ...themeConfig };
            } catch (e) {
                console.error('Could not parse theme preferences');
            }
        }
        this.config = signal<AppConfig>(config);

        effect(() => {
            const config = this.config();
            this.applyConfig(this._initialLoad);
            this._initialLoad = false;
        });

        this.hotkeyManager.assignHotkey("shift", () => {
            if (this.isOverlay()){
                this.onMenuToggle();
            }
        });
    }

    getThemeById(id: ThemeId): ThemeDefinition {
        return APP_THEMES.find(t => t.id === id) ?? APP_THEMES[0];
    }

    applyConfig(force = false) {
        const config = this.config();
        if (force || this.updateStyle(config)) {
            this.changeTheme();
        }
        this.changeScale(config.scale);
        this.applyCardRadius(config.cardRadius);
        this.state.update((state) => ({
            ...state,
            staticMenuDesktopInactive: false,
            staticMenuMobileActive: false,
        }));
        this.onConfigUpdate();
    }

    resetTheme()
    {
        this.config.set(this.defaultTheme);
        this.storageService.removeItem('themePrefs');
    }

    updateStyle(config: AppConfig) {
        return config.theme !== this._config.theme;
    }

    changeTheme() {
        const theme = this.getThemeById(this.config().theme);
        const isDark = theme.colorScheme === 'dark';

        // Reset to base AppTheme, then apply theme-specific overrides
        usePreset(AppTheme);
        if (Object.keys(theme.preset).length > 0) {
            updatePreset(theme.preset);
        }

        // Apply sidebar CSS variables on :root
        this.applySidebarVars(theme.sidebar);

        // Apply storefront CSS custom properties on :root
        this.applyStorefrontVars(getStorefrontVars(theme));

        // Swap Syncfusion CSS bundle (light <-> dark)
        const themeCss = isDark ? 'theme-dark.css' : 'theme-light.css';
        this.replaceThemeLink(themeCss, 'theme-link');

        // Toggle PrimeNG dark mode class on <html>
        const element = document.documentElement;
        element.classList.toggle('dark-theme', isDark);
    }

    applySidebarVars(sidebar: SidebarVars) {
        const style = document.documentElement.style;
        style.setProperty('--d-sidebar-bg-color', sidebar.bgColor);
        style.setProperty('--d-sidebar-bg-color-alt', sidebar.bgColorAlt);
        style.setProperty('--d-sidebar-border', sidebar.border);
        style.setProperty('--d-app-name-color', sidebar.appNameColor);
        style.setProperty('--d-menu-separator-border', sidebar.menuSeparatorBorder);
        style.setProperty('--d-menuitem-root-text-color', sidebar.menuitemRootTextColor);
        style.setProperty('--d-menuitem-text-color', sidebar.menuitemTextColor);
        style.setProperty('--d-menuitem-hover-bg', sidebar.menuitemHoverBg);
        style.setProperty('--d-menuitem-active-bg', sidebar.menuitemActiveBg);
        style.setProperty('--d-menuitem-text-active-color', sidebar.menuitemTextActiveColor);
        style.setProperty('--d-menuitem-focus-shadow', sidebar.menuitemFocusShadow);
    }

    applyStorefrontVars(storefront: StorefrontVars) {
        const style = document.documentElement.style;
        // Legacy brand-specific vars
        style.setProperty('--store-gold', storefront.gold);
        style.setProperty('--store-gold-dim', storefront.goldDim);
        style.setProperty('--store-gold-faint', storefront.goldFaint);
        style.setProperty('--store-gold-hover', storefront.goldHover);
        style.setProperty('--store-gold-contrast', storefront.goldContrast);
        style.setProperty('--store-crimson', storefront.crimson);
        style.setProperty('--store-crimson-dim', storefront.crimsonDim);
        style.setProperty('--store-crimson-contrast', storefront.crimsonContrast);
        style.setProperty('--store-header-bg', storefront.headerBg);
        style.setProperty('--store-header-bg-scrolled', storefront.headerBgScrolled);
        style.setProperty('--store-logo-filter', storefront.logoFilter);

        // Monochrome palette tokens
        style.setProperty('--store-bg', storefront.bg);
        style.setProperty('--store-surface', storefront.surface);
        style.setProperty('--store-surface-alt', storefront.surfaceAlt);
        style.setProperty('--store-text', storefront.text);
        style.setProperty('--store-text-muted', storefront.textMuted);
        style.setProperty('--store-primary', storefront.primary);
        style.setProperty('--store-primary-contrast', storefront.primaryContrast);
        style.setProperty('--store-accent', storefront.accent);
        style.setProperty('--store-accent-hover', storefront.accentHover);
        style.setProperty('--store-accent-contrast', storefront.accentContrast);
        style.setProperty('--store-border', storefront.border);
        style.setProperty('--store-border-subtle', storefront.borderSubtle);
    }

    replaceThemeLink(href: string, id: string) {
        let themeLink = <HTMLLinkElement>document.getElementById(id);
        const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        themeLink.parentNode!.insertBefore(
            cloneLinkElement,
            themeLink.nextSibling
        );
        cloneLinkElement.addEventListener('load', () => {
            themeLink.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    changeScale(value: number) {
        document.documentElement.style.fontSize = `${value}px`;
    }

    private static readonly CARD_RADIUS_MAP: Record<CardRadius, { css: string; primitives: Record<string, string> }> = {
        square: {
            css: '0',
            primitives: { none: '0', xs: '0', sm: '0', md: '0', lg: '0', xl: '0' },
        },
        small: {
            css: '4px',
            primitives: { none: '0', xs: '1px', sm: '2px', md: '3px', lg: '4px', xl: '8px' },
        },
        medium: {
            css: '8px',
            primitives: { none: '0', xs: '2px', sm: '4px', md: '6px', lg: '8px', xl: '12px' },
        },
        large: {
            css: '12px',
            primitives: { none: '0', xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px' },
        },
    };

    applyCardRadius(radius: CardRadius) {
        const mapping = LayoutService.CARD_RADIUS_MAP[radius] ?? LayoutService.CARD_RADIUS_MAP['small'];
        // Update the legacy --border-radius CSS var used by .card class
        document.documentElement.style.setProperty('--border-radius', mapping.css);
        // Update PrimeNG primitive border-radius tokens
        updatePreset({ primitive: { borderRadius: mapping.primitives } });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.state.update((state) => ({
                ...state,
                overlayMenuActive: !state.overlayMenuActive,
            }));

            if (this.state().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.state.update((state) => ({
                ...state,
                staticMenuDesktopInactive: !state.staticMenuDesktopInactive,
            }));
        } else {
            this.state.update((state) => ({
                ...state,
                staticMenuMobileActive: !state.staticMenuMobileActive,
            }));

            if (this.state().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    onOverlaySubmenuOpen() {
        this.overlayOpen.next(null);
    }

    showRightMenu() {
        this.state.update((state) => ({
            ...state,
            rightMenuVisible: true,
        }));
    }

    toggleSearchBar() {
        this.state.update((state) => ({
            ...state,
            searchBarActive: !state.searchBarActive,
        }));
    }

    isOverlay() {
        return this.config().menuMode === 'overlay';
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isSlim() {
        return this.config().menuMode === 'slim';
    }

    isCompact() {
        return this.config().menuMode === 'compact';
    }

    isHorizontal() {
        return this.config().menuMode === 'horizontal';
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.config() };
        this.configUpdate.next(this.config());
        this.storageService.setItem('themePrefs', JSON.stringify(this.config()));
    }

    showTopBarBreadCrumb = computed(() => {
        // static or overlay or Slim
        let mode = this.config().menuMode;
        let show = mode === 'static' || mode === 'overlay' || mode === 'slim';

        return show;
    });

    showTopPageBreadCrumb = computed(() => {
        // compact or horizontal
        let mode = this.config().menuMode;
        let show = mode === 'horizontal';

        return show;
    });

}
